import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { useRecoilValue } from 'recoil';

import type * as api from '@gitbook/api-client';
import type { CommentContext } from '@gitbook/app-database';
import type { NavigationCommentsSideSheet } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { type Document, serialize } from '@gitbook/slate';
import { assertIsDefined } from '@gitbook/util-ts';

import { useNavigation, useNavigationState } from '../../navigation';
import type { NavigationState } from '../../navigation/types';
import {
    apiClient,
    apiQuery,
    getSerializedTableCellKey,
    isCommentNodeHoveredSelector,
} from '../../state';
import { useCurrentHivePage } from '../Hive/useCurrentHivePage';
import { useListPaginatedComments } from './useListPaginatedComments';

interface CommentsMap {
    [key: string]: api.Comment[] | undefined;
}

export const CommentsContext = React.createContext<CommentsMap | null>(null);

/** Key used for page-level comments (comments not attached to any specific node) */
const PAGE_LEVEL_COMMENT_KEY = 'PAGE_LEVEL_COMMENTS';

export type CommentTarget = string | 'page';

export type CommentUISurface = 'none' | 'popover' | 'side-panel';
export type CommentUIIntent = 'idle' | 'create' | 'view';

export interface UseCommentsProps {
    /** The node (e.g. block) to read and write comments on */
    nodeKey: CommentTarget;
    /** Table cell details if the comment is on a table cell */
    tableCell?: api.TableCellTarget;
}

export interface UseCommentsReturn {
    /** Comments for the node */
    nodeComments: api.Comment[];
    /** Whether there are comments on this node */
    hasComments: boolean;
    /** Counts for the number of comments on this node */
    commentCount: {
        total: number;
        topLevel: number;
    };
    /** The active comment */
    activeComment: api.Comment | null;
    /** High-level description of how comments should render for this node */
    commentUI: {
        surface: CommentUISurface;
        intent: CommentUIIntent;
        isTargeted: boolean;
    };
    /** Submit a new comment */
    submitComment: (body: Document) => Promise<void>;
    /** Open the comments UI (sidebar or popover) for this node */
    openNodeComments: (intent?: CommentUIIntent) => void;
    /** Open a provided comment thread */
    openCommentThread: (comment: api.Comment | null) => void;
    /** Close the popover */
    closePopover: () => void;
    /** State of the node related to comments UI */
    nodeState: {
        /** Is a comment linked to this node currently hovered */
        isHovered: boolean;
        /** Is this node also in the current navigation state */
        isOpened: boolean;
        /** Whether the add comment popover is open */
        addNewPopoverIsOpen: boolean;
        /** Whether the read comments popover is open */
        readPopoverIsOpen: boolean;
        /** Where the comments UI is being rendered */
        uiSurface: CommentUISurface;
        /** The current intent of the comments UI for this node */
        uiIntent: CommentUIIntent;
    };
}

/**
 * A context provider for comments on a document. Returns a stable ref to a map of node
 * keys to comments.
 */
export function DocumentCommentsProvider(props: {
    skip: boolean;
    spaceId: string;
    pageId: string;
    changeRequestId?: string;
    children: React.ReactNode;
}): React.ReactElement {
    const { skip, spaceId, pageId, changeRequestId, children } = props;

    const [comments] = useListPaginatedComments({
        spaceId,
        changeRequestId,
        pageId,
        status: 'open',
        skip,

        // We don't want to suspense here because we don't want to block the UI
        // when fetching comments.
        suspense: false,
    });

    const commentsByKey = React.useMemo<CommentsMap>(() => {
        const _commentsByKey: CommentsMap = {};
        if (!comments.length) {
            return _commentsByKey;
        }

        for (const comment of comments) {
            const key = comment.target.node?.id ?? PAGE_LEVEL_COMMENT_KEY;

            if (!_commentsByKey[key]) {
                _commentsByKey[key] = [comment];
            } else {
                _commentsByKey[key]?.push(comment);
            }
        }

        return _commentsByKey;
    }, [comments]);

    return <CommentsContext.Provider value={commentsByKey}>{children}</CommentsContext.Provider>;
}

/**
 * Hook to provide comment state and handlers for any block or node.
 */
export function useDocumentNodeComments({
    nodeKey,
    tableCell,
}: UseCommentsProps): UseCommentsReturn {
    const { navigate } = useNavigation();
    const { screen } = useNavigationState();
    const { revision } = useHiveRevision();
    const pageId = screen.type === 'space' ? screen.page : '';
    const [currentPage] = useCurrentHivePage(revision, pageId);

    const commentsByKey = React.useContext(CommentsContext);
    // TODO: wrap the new change request screen in a DocumentCommentsProvider
    assertIsDefined(commentsByKey, 'useComments must be used within a CommentsContextProvider');
    const blockComments = commentsByKey?.[nodeKey];
    const comments = React.useMemo(() => {
        if (!blockComments) {
            return [];
        }
        if (!tableCell?.record || !tableCell?.definition) {
            return blockComments;
        }

        return blockComments.filter(
            (comment) =>
                comment.target.tableCell &&
                comment.target.tableCell.record === tableCell.record &&
                comment.target.tableCell.definition === tableCell.definition
        );
    }, [blockComments, tableCell?.record, tableCell?.definition]);

    const changeRequestId = screen.type === 'space' ? screen.changeRequest?.toString() : undefined;

    const { data: changeRequest } = useSuspenseQuery(
        apiQuery(
            'getChangeRequestById',
            changeRequestId
                ? {
                      spaceId: screen.type === 'space' ? screen.space : '',
                      changeRequestId,
                  }
                : skipToken
        )
    );

    // Get comments sideSheet state
    const commentsSideSheet = React.useMemo(() => getCommentsSideSheet(screen), [screen]);

    // Count all open top-level comments including their replies
    const numberOfComments = React.useMemo(
        () =>
            comments
                .filter((comment: api.Comment) => comment.status === 'open')
                .reduce((acc, curr) => acc + 1 + (curr.replies || 0), 0),
        [comments]
    );

    const commentCount = React.useMemo(
        () => ({
            total: numberOfComments,
            topLevel: comments.length,
        }),
        [comments.length, numberOfComments]
    );

    const hasComments = comments.length > 0;
    const urlParamContext = getUrlContextParam(screen, hasComments);

    // Hover state: check whether a comment attached to this node is hovered
    const hoverKey = React.useMemo(
        () => getSerializedTableCellKey(tableCell) || nodeKey,
        [tableCell, nodeKey]
    );
    const hasCommentHovered = useRecoilValue(isCommentNodeHoveredSelector(hoverKey || ''));

    const commentUI = React.useMemo(
        () =>
            resolveNodeCommentUI({
                sideSheet: commentsSideSheet,
                nodeKey,
                tableCell,
                hasComments,
            }),
        [commentsSideSheet, nodeKey, tableCell, hasComments]
    );

    const addNewPopoverIsOpen = commentUI.surface === 'popover' && commentUI.intent === 'create';
    const readPopoverIsOpen = commentUI.surface === 'popover' && commentUI.intent === 'view';

    // Comment context for creating new comments
    const context: CommentContext = React.useMemo(
        () => ({
            space: screen.type === 'space' ? screen.space : '',
            changeRequest: changeRequest?.id || null,
            page: currentPage?.id,

            // If creating a comment for this node, include node context
            ...(nodeKey
                ? {
                      node: nodeKey,
                      pageSheet: currentPage?.document,
                      ...(tableCell
                          ? {
                                tableCell: {
                                    record: tableCell.record,
                                    definition: tableCell.definition,
                                },
                            }
                          : {}),
                  }
                : {}),
        }),
        [screen, changeRequest?.id, currentPage, nodeKey, tableCell]
    );

    const submitComment = async (body: Document) => {
        const data: api.PostCommentSchema = {
            node: context.node || undefined,
            page: context.page,
            body: {
                document: serialize(body),
            },
            ...(context.tableCell ? { tableCell: context.tableCell } : {}),
        };

        const response = context.changeRequest
            ? await apiClient.changeRequestComments.postCommentInChangeRequest(
                  context.space,
                  context.changeRequest,
                  data
              )
            : await apiClient.spaceComments.postCommentInSpace(context.space, data);

        const comment = response.data;

        navigate({
            screen: {
                sideSheet: {
                    type: 'comments',
                    comment: comment.id,
                    node: comment.target.node ? comment.target.node.id : undefined,
                    context: urlParamContext,
                    ...(context.tableCell ? { tableCell: context.tableCell } : {}),
                },
            },
        });
    };

    // Get the active comment object from the sideSheet comment id, if any.
    const activeComment = React.useMemo(() => {
        return comments.find((comment) => comment.id === commentsSideSheet?.comment) || null;
    }, [comments, commentsSideSheet]);

    const resolveNodeContext = React.useCallback(
        (comment: api.Comment | null) => {
            const commentNodeKey =
                comment?.target.node?.id ??
                (!comment && typeof nodeKey === 'string' ? nodeKey : undefined);
            const tableCellTarget = comment?.target.tableCell ?? (!comment ? tableCell : undefined);

            return { nodeId: commentNodeKey, tableCellTarget };
        },
        [nodeKey, tableCell]
    );

    // Open the isolated details view of a specific comment thread
    const openCommentThread = React.useCallback(
        (comment: api.Comment | null) => {
            const { nodeId, tableCellTarget } = resolveNodeContext(comment);

            // Try to locate the page in the current revision first (if any)
            const page = comment?.target.page
                ? hiveCore.getAbsolutePagePathInRevision(revision, comment.target.page.id)
                : undefined;

            navigate(
                {
                    current: {
                        ...(typeof page !== 'undefined' ? { page } : {}),
                        sideSheet: {
                            type: 'comments',
                            context: urlParamContext,
                            node: nodeId,
                            comment: comment?.id ?? undefined,
                            tableCell: tableCellTarget
                                ? {
                                      record: tableCellTarget.record,
                                      definition: tableCellTarget.definition,
                                  }
                                : undefined,
                        },
                    },
                },
                {
                    replace: true,
                }
            );
        },
        [navigate, revision, resolveNodeContext, urlParamContext]
    );

    // Navigate to the current node in the url navigation state. Accepts an `intent` to override the default automatic logic to decide on opening a panel vs popover based on states.
    const openNodeComments = React.useCallback(
        (intent?: CommentUIIntent) => {
            if (screen.type !== 'space') {
                // Can only create comments in space screens
                return;
            }

            return navigate({
                screen: {
                    ...screen,
                    sideSheet: {
                        type: 'comments',
                        node: nodeKey,
                        comment: undefined,
                        // By default, the urlParamContext determines whether to open a panel or a popover based on the presence of comments and whether the panel is already open. In some cases (like the comment UI for table cells), we pass an explicit `intent`.
                        context: intent === 'create' ? 'preview' : urlParamContext,
                        ...(tableCell
                            ? {
                                  tableCell: {
                                      record: tableCell.record,
                                      definition: tableCell.definition,
                                  },
                              }
                            : {}),
                    },
                },
            });
        },
        [navigate, screen, nodeKey, tableCell, urlParamContext]
    );

    /** Closes the popover by removing the comments from navigation state */
    const closePopover = () => {
        navigate({
            current: {
                sideSheet: undefined,
            },
        });
    };

    return {
        hasComments,
        activeComment,
        commentCount,
        commentUI,
        submitComment,
        openNodeComments,
        openCommentThread,
        closePopover,
        nodeComments: comments,
        nodeState: {
            isHovered: hasCommentHovered,
            isOpened: commentUI.isTargeted,
            readPopoverIsOpen,
            addNewPopoverIsOpen,
            uiSurface: commentUI.surface,
            uiIntent: commentUI.intent,
        },
    };
}

/**
 * Helper to get the comments sideSheet from navigation state
 */
function getCommentsSideSheet(
    screen: NavigationState['screen']
): NavigationCommentsSideSheet | undefined {
    if (screen.type !== 'space' || !screen.sideSheet || screen.sideSheet.type !== 'comments') {
        return undefined;
    }
    return screen.sideSheet as NavigationCommentsSideSheet;
}

/**
 * Helper to decide whether the comment URL navigation state should open in a sidepanel or popover.
 * If the sideSheet is open (i.e. type === 'comments'), adopt the existing UI (sidepanel or popover).
 * Else, we always default to showing popovers.
 */
const getUrlContextParam = (
    screen: NavigationState['screen'],
    hasComments: boolean
): 'preview' | undefined => {
    if (screen.type !== 'space') {
        return undefined;
    }

    // We already have comments UI open (can be sidepanel (undefined context) or popover (context: 'preview')
    if (screen.sideSheet && screen.sideSheet.type === 'comments' && hasComments) {
        return screen.sideSheet.context;
    } else {
        return 'preview';
    }
};

interface ResolveNodeCommentUIArgs {
    sideSheet: NavigationCommentsSideSheet | undefined;
    nodeKey: CommentTarget;
    tableCell?: api.TableCellTarget;
    hasComments: boolean;
}

interface NodeCommentUIState {
    surface: CommentUISurface;
    intent: CommentUIIntent;
    isTargeted: boolean;
    hasActiveComment: boolean;
}

function resolveNodeCommentUI(args: ResolveNodeCommentUIArgs): NodeCommentUIState {
    const { sideSheet, nodeKey, tableCell, hasComments } = args;

    if (!sideSheet || sideSheet.type !== 'comments') {
        return {
            surface: 'none',
            intent: 'idle',
            isTargeted: false,
            hasActiveComment: false,
        };
    }

    const surface: CommentUISurface = sideSheet.context === 'preview' ? 'popover' : 'side-panel';
    const isTargeted = matchesCommentTarget(sideSheet, nodeKey, tableCell);
    const hasActiveComment = Boolean(sideSheet.comment);

    if (!isTargeted) {
        return {
            surface,
            intent: 'idle',
            isTargeted,
            hasActiveComment,
        };
    }

    const hasSomethingToShow = hasActiveComment || hasComments;
    const intent: CommentUIIntent =
        surface === 'popover'
            ? hasSomethingToShow
                ? 'view'
                : 'create'
            : hasSomethingToShow
              ? 'view'
              : 'idle';

    return {
        surface,
        intent,
        isTargeted,
        hasActiveComment,
    };
}

function matchesCommentTarget(
    sideSheet: NavigationCommentsSideSheet,
    nodeKey: CommentTarget,
    tableCell?: api.TableCellTarget
): boolean {
    if (sideSheet.type !== 'comments') {
        return false;
    }

    if (!matchesNodeKey(sideSheet.node, nodeKey)) {
        return false;
    }

    if (!tableCell && !sideSheet.tableCell) {
        return true;
    }

    if (!tableCell || !sideSheet.tableCell) {
        return false;
    }

    return (
        sideSheet.tableCell.record === tableCell.record &&
        sideSheet.tableCell.definition === tableCell.definition
    );
}

function matchesNodeKey(sideSheetNode: string | undefined, nodeKey: CommentTarget): boolean {
    if (!nodeKey) {
        return !sideSheetNode;
    }

    return sideSheetNode === nodeKey;
}
